import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Briefcase, CheckCircle, Clock, MapPin,
  DollarSign, Building2, Filter, Tag, FileText, Pencil,
  Upload, Download, Archive, UserCheck, XCircle, Paperclip, Trash2, MessageSquare,
} from "lucide-react";
import api from "../lib/api";

const jobTypes = [
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "contract", label: "Contract" },
  { value: "internship", label: "Internship" },
];

const applicationStatuses = [
  { value: "new", label: "New", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "screening", label: "Screening", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "interview", label: "Interview", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "offer", label: "Offer", color: "bg-green-50 text-green-700 border-green-200" },
  { value: "hired", label: "Hired", color: "bg-green-100 text-green-800 border-green-300" },
  { value: "rejected", label: "Rejected", color: "bg-red-50 text-red-700 border-red-200" },
];

const ACTIVE_STATUSES = ["new", "screening", "interview", "offer"];
const ARCHIVED_STATUSES = ["hired", "rejected"];

const Recruitment = () => {
  const { currentBusiness, user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [importing, setImporting] = useState(false);
  const csvRef = useRef(null);
  const resumeRef = useRef(null);
  const editResumeRef = useRef(null);

  // Filters
  const [jobDeptFilter, setJobDeptFilter] = useState("all");
  const [jobDesigFilter, setJobDesigFilter] = useState("all");
  const [appDeptFilter, setAppDeptFilter] = useState("all");
  const [appDesigFilter, setAppDesigFilter] = useState("all");

  const [jobFormData, setJobFormData] = useState({
    title: "", department_id: "", designation_id: "", description: "",
    requirements: "", salary_range: "", location: "", job_type: "full_time"
  });
  const [appFormData, setAppFormData] = useState({
    job_id: "", applicant_name: "", applicant_email: "", applicant_phone: "", cover_letter: ""
  });

  useEffect(() => {
    if (currentBusiness?.id) fetchData();
  }, [currentBusiness?.id]);

  const fetchData = async () => {
    try {
      const [jobsRes, appsRes, deptRes, desigRes] = await Promise.all([
        api.get(`/jobs?business_id=${currentBusiness.id}`),
        api.get(`/applications?business_id=${currentBusiness.id}`),
        api.get(`/departments?business_id=${currentBusiness.id}`),
        api.get(`/designations?business_id=${currentBusiness.id}`),
      ]);
      setJobs(jobsRes.data);
      setApplications(appsRes.data);
      setDepartments(deptRes.data);
      setDesignations(desigRes.data);
    } catch { toast.error("Failed to fetch recruitment data"); }
    finally { setLoading(false); }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", { ...jobFormData, business_id: currentBusiness.id });
      toast.success("Job posting created");
      setJobDialogOpen(false);
      setJobFormData({ title: "", department_id: "", designation_id: "", description: "", requirements: "", salary_range: "", location: "", job_type: "full_time" });
      fetchData();
    } catch { toast.error("Failed to create job posting"); }
  };

  const handleCreateApplication = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/applications", { ...appFormData, business_id: currentBusiness.id });
      const newAppId = res.data.id;
      // Upload resume if selected
      if (resumeRef.current?.files?.[0]) {
        const fd = new FormData();
        fd.append("file", resumeRef.current.files[0]);
        await api.post(`/applications/${newAppId}/resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Application submitted");
      setAppDialogOpen(false);
      setAppFormData({ job_id: "", applicant_name: "", applicant_email: "", applicant_phone: "", cover_letter: "" });
      if (resumeRef.current) resumeRef.current.value = "";
      fetchData();
    } catch { toast.error("Failed to submit application"); }
  };

  const handleCloseJob = async (id) => {
    try { await api.put(`/jobs/${id}/close`); toast.success("Job closed"); fetchData(); }
    catch { toast.error("Failed to close job"); }
  };

  const handleUpdateApplicationStatus = async (id, status) => {
    try { await api.put(`/applications/${id}/status?status=${status}`); toast.success("Status updated"); fetchData(); }
    catch { toast.error("Failed to update status"); }
  };

  const openEditDialog = (app) => {
    setEditApp({ ...app });
    setEditDialogOpen(true);
  };

  const handleEditApplication = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/applications/${editApp.id}`, {
        applicant_name: editApp.applicant_name,
        applicant_email: editApp.applicant_email,
        applicant_phone: editApp.applicant_phone,
        cover_letter: editApp.cover_letter,
        job_id: editApp.job_id,
        notes: editApp.notes,
      });
      // Upload new resume if selected
      if (editResumeRef.current?.files?.[0]) {
        const fd = new FormData();
        fd.append("file", editResumeRef.current.files[0]);
        await api.post(`/applications/${editApp.id}/resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Application updated");
      setEditDialogOpen(false);
      setEditApp(null);
      if (editResumeRef.current) editResumeRef.current.value = "";
      fetchData();
    } catch { toast.error("Failed to update application"); }
  };

  const handleDeleteResume = async (appId) => {
    try {
      await api.delete(`/applications/${appId}/resume`);
      toast.success("Resume removed");
      if (editApp?.id === appId) {
        setEditApp(prev => ({ ...prev, resume_url: null, resume_filename: null }));
      }
      fetchData();
    } catch { toast.error("Failed to remove resume"); }
  };

  const handleDownloadResume = async (appId) => {
    try {
      const res = await api.get(`/applications/${appId}/resume/download`, { responseType: "blob" });
      const app = applications.find(a => a.id === appId);
      const filename = app?.resume_filename || "resume";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download resume"); }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/applications/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { created, errors } = res.data;
      toast.success(`Imported ${created} application(s)`);
      if (errors?.length > 0) {
        toast.warning(`${errors.length} row(s) had issues`, { description: errors.slice(0, 3).join("; ") });
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import CSV");
    } finally {
      setImporting(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const headers = "Applicant Name,Email,Phone,Position,Cover Letter";
    const example = "John Doe,john@email.com,+91 98765 43210,Senior Engineer,Passionate about the role";
    const blob = new Blob([headers + "\n" + example + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "applications_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const getDeptName = (id) => departments.find(d => d.id === id)?.name || "—";
  const getDesigName = (id) => designations.find(d => d.id === id)?.name || "—";
  const getJobTitle = (id) => jobs.find(j => j.id === id)?.title || "Unknown";
  const getJobTypeLabel = (v) => jobTypes.find(t => t.value === v)?.label || v;
  const getStatusInfo = (s) => applicationStatuses.find(st => st.value === s) || applicationStatuses[0];

  // Filtered lists
  const openFilteredJobs = jobs.filter(j => {
    if (j.status !== "open") return false;
    if (jobDeptFilter !== "all" && j.department_id !== jobDeptFilter) return false;
    if (jobDesigFilter !== "all" && j.designation_id !== jobDesigFilter) return false;
    return true;
  });
  const closedJobs = jobs.filter(j => j.status === "closed");

  const filterByDeptDesig = (a) => {
    const job = jobs.find(j => j.id === a.job_id);
    if (!job) return true;
    if (appDeptFilter !== "all" && job.department_id !== appDeptFilter) return false;
    if (appDesigFilter !== "all" && job.designation_id !== appDesigFilter) return false;
    return true;
  };

  const activeApps = applications.filter(a => ACTIVE_STATUSES.includes(a.status)).filter(filterByDeptDesig);
  const archivedApps = applications.filter(a => ARCHIVED_STATUSES.includes(a.status)).filter(filterByDeptDesig);
  const hiredCount = applications.filter(a => a.status === "hired").length;
  const rejectedCount = applications.filter(a => a.status === "rejected").length;

  const openJobs = jobs.filter(j => j.status === "open").length;
  const newApps = applications.filter(a => a.status === "new").length;
  const canManage = ["super_admin", "business_admin", "hr_manager"].includes(user?.role);

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business to manage recruitment</p>
      </div>
    );
  }

  const FilterBar = ({ deptFilter, setDeptFilter, desigFilter, setDesigFilter, testPrefix }) => (
    <div className="flex flex-wrap gap-3 items-center" data-testid={`${testPrefix}-filters`}>
      <Filter className="h-4 w-4 text-slate-400" />
      <Select value={deptFilter} onValueChange={setDeptFilter}>
        <SelectTrigger className="w-[180px] h-9 text-sm input-field" data-testid={`${testPrefix}-dept-filter`}>
          <SelectValue placeholder="All Departments" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Departments</SelectItem>
          {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={desigFilter} onValueChange={setDesigFilter}>
        <SelectTrigger className="w-[180px] h-9 text-sm input-field" data-testid={`${testPrefix}-desig-filter`}>
          <SelectValue placeholder="All Designations" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Designations</SelectItem>
          {designations.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {(deptFilter !== "all" || desigFilter !== "all") && (
        <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => { setDeptFilter("all"); setDesigFilter("all"); }}
          data-testid={`${testPrefix}-clear-filters`}>
          Clear filters
        </Button>
      )}
    </div>
  );

  const ApplicationTable = ({ apps, showArchiveBadge = false, testId }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Applicant</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Position</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Department</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Contact</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Resume</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Notes</TableHead>
            <TableHead className="text-xs uppercase tracking-wide text-slate-400">Status</TableHead>
            {canManage && <TableHead className="text-xs uppercase tracking-wide text-slate-400 text-right">Action</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {apps.map(app => {
            const statusInfo = getStatusInfo(app.status);
            const job = jobs.find(j => j.id === app.job_id);
            return (
              <TableRow key={app.id} className="hover:bg-slate-50/50" data-testid={`app-row-${app.id}`}>
                <TableCell className="font-medium text-slate-800">{app.applicant_name}</TableCell>
                <TableCell className="text-slate-600">
                  <div>{getJobTitle(app.job_id)}</div>
                  <div className="flex gap-1 mt-0.5">
                    {job?.department_id && <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 text-[10px] px-1.5 py-0">{getDeptName(job.department_id)}</Badge>}
                  </div>
                </TableCell>
                <TableCell>
                  {job?.designation_id ? (
                    <Badge variant="outline" className="bg-violet-50 text-violet-600 border-violet-200 text-xs">{getDesigName(job.designation_id)}</Badge>
                  ) : <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-slate-600">
                  <div className="text-sm">{app.applicant_email}</div>
                  {app.applicant_phone && <div className="text-xs text-slate-400">{app.applicant_phone}</div>}
                </TableCell>
                <TableCell>
                  {app.resume_filename ? (
                    <button
                      onClick={() => handleDownloadResume(app.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-600 text-xs font-medium hover:bg-emerald-100 transition-colors"
                      data-testid={`download-resume-${app.id}`}
                      title={app.resume_filename}
                    >
                      <Paperclip className="h-3 w-3" />
                      CV
                    </button>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {app.notes ? (
                    <button
                      onClick={() => openEditDialog(app)}
                      className="group inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-600 text-xs font-medium hover:bg-amber-100 transition-colors max-w-[140px]"
                      data-testid={`notes-indicator-${app.id}`}
                      title={app.notes}
                    >
                      <MessageSquare className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{app.notes}</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => openEditDialog(app)}
                      className="text-slate-300 hover:text-amber-500 transition-colors"
                      data-testid={`add-notes-${app.id}`}
                      title="Add notes"
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                    </button>
                  )}
                </TableCell>
                <TableCell><Badge variant="outline" className={statusInfo.color}>{statusInfo.label}</Badge></TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-primary"
                        onClick={() => openEditDialog(app)} data-testid={`edit-app-${app.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Select value={app.status} onValueChange={v => handleUpdateApplicationStatus(app.id, v)}>
                        <SelectTrigger className="w-28 h-8 text-xs" data-testid={`app-status-select-${app.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {applicationStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Recruitment</h1>
          <p className="text-slate-500">Manage job postings and applications</p>
        </div>
        {canManage && (
          <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-job-btn"><Plus className="h-4 w-4 mr-2" /> Post Job</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Create Job Posting</DialogTitle>
                <DialogDescription>Add a new job opening</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateJob} className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
                <div className="space-y-2">
                  <Label>Job Title</Label>
                  <Input value={jobFormData.title} onChange={e => setJobFormData({ ...jobFormData, title: e.target.value })}
                    placeholder="Senior Software Engineer" className="input-field" required data-testid="job-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select value={jobFormData.department_id || "none"} onValueChange={v => setJobFormData({ ...jobFormData, department_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="input-field" data-testid="job-dept-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Designation</Label>
                    <Select value={jobFormData.designation_id || "none"} onValueChange={v => setJobFormData({ ...jobFormData, designation_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="input-field" data-testid="job-desig-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {designations.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Job Type</Label>
                    <Select value={jobFormData.job_type} onValueChange={v => setJobFormData({ ...jobFormData, job_type: v })}>
                      <SelectTrigger className="input-field" data-testid="job-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {jobTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input value={jobFormData.location} onChange={e => setJobFormData({ ...jobFormData, location: e.target.value })}
                      placeholder="Mumbai, India" className="input-field" data-testid="job-location-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Salary Range</Label>
                  <Input value={jobFormData.salary_range} onChange={e => setJobFormData({ ...jobFormData, salary_range: e.target.value })}
                    placeholder="6L - 12L" className="input-field" data-testid="job-salary-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={jobFormData.description} onChange={e => setJobFormData({ ...jobFormData, description: e.target.value })}
                    placeholder="Job description..." className="input-field min-h-[80px]" required data-testid="job-desc-input" />
                </div>
                <div className="space-y-2">
                  <Label>Requirements</Label>
                  <Textarea value={jobFormData.requirements} onChange={e => setJobFormData({ ...jobFormData, requirements: e.target.value })}
                    placeholder="Requirements..." className="input-field min-h-[80px]" data-testid="job-req-input" />
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="save-job-btn">Create Job Posting</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10"><Briefcase className="h-6 w-6 text-primary" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{jobs.length}</p><p className="text-sm text-slate-500">Total Jobs</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100"><CheckCircle className="h-6 w-6 text-green-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{openJobs}</p><p className="text-sm text-slate-500">Open Positions</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-violet-100"><FileText className="h-6 w-6 text-violet-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{applications.length}</p><p className="text-sm text-slate-500">Total Applications</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100"><Clock className="h-6 w-6 text-blue-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{newApps}</p><p className="text-sm text-slate-500">New Applications</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="jobs" className="space-y-6">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-full">
          <TabsTrigger value="jobs" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="jobs-tab">
            Open Positions ({openFilteredJobs.length})
          </TabsTrigger>
          <TabsTrigger value="applications" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="applications-tab">
            Applications ({activeApps.length})
          </TabsTrigger>
          <TabsTrigger value="archived" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="archived-tab">
            <Archive className="h-3.5 w-3.5 mr-1.5" />
            Archived ({archivedApps.length + closedJobs.length})
          </TabsTrigger>
        </TabsList>

        {/* Jobs Tab */}
        <TabsContent value="jobs">
          <div className="space-y-4">
            <FilterBar deptFilter={jobDeptFilter} setDeptFilter={setJobDeptFilter}
              desigFilter={jobDesigFilter} setDesigFilter={setJobDesigFilter} testPrefix="jobs" />
            <Card className="card-soft" data-testid="jobs-table">
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>
                ) : openFilteredJobs.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    {jobs.filter(j => j.status === "open").length === 0 ? "No open positions. Create your first one." : "No jobs match the selected filters."}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {openFilteredJobs.map(job => (
                      <Card key={job.id} className="border border-slate-100 hover:shadow-md transition-shadow" data-testid={`job-card-${job.id}`}>
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-semibold text-slate-800">{job.title}</h3>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {job.department_id && (
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                                    <Building2 className="h-3 w-3 mr-1" />{getDeptName(job.department_id)}
                                  </Badge>
                                )}
                                {job.designation_id && (
                                  <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-xs">
                                    <Tag className="h-3 w-3 mr-1" />{getDesigName(job.designation_id)}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className={job.status === "open" ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-50 text-slate-600 border-slate-200"}>
                              {job.status}
                            </Badge>
                          </div>
                          <div className="space-y-1.5 text-sm text-slate-600">
                            <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-slate-400" />{getJobTypeLabel(job.job_type)}</div>
                            {job.location && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{job.location}</div>}
                            {job.salary_range && <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-slate-400" />{job.salary_range}</div>}
                          </div>
                          <p className="text-xs text-slate-400 mt-2">
                            {applications.filter(a => a.job_id === job.id).length} application(s)
                          </p>
                          {canManage && job.status === "open" && (
                            <div className="mt-3">
                              <Button variant="outline" size="sm" onClick={() => handleCloseJob(job.id)}
                                className="rounded-full w-full" data-testid={`close-job-${job.id}`}>Close Job</Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Active Applications Tab */}
        <TabsContent value="applications">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <FilterBar deptFilter={appDeptFilter} setDeptFilter={setAppDeptFilter}
                desigFilter={appDesigFilter} setDesigFilter={setAppDesigFilter} testPrefix="apps" />
              <div className="flex items-center gap-2">
                {/* CSV Import */}
                <input type="file" accept=".csv" ref={csvRef} className="hidden" onChange={handleCsvImport} data-testid="csv-file-input" />
                <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={downloadTemplate} data-testid="download-csv-template-btn">
                  <Download className="h-3.5 w-3.5" /> Template
                </Button>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5" disabled={importing}
                  onClick={() => csvRef.current?.click()} data-testid="import-csv-btn">
                  <Upload className="h-3.5 w-3.5" /> {importing ? "Importing..." : "Import CSV"}
                </Button>
                <Dialog open={appDialogOpen} onOpenChange={setAppDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-full" data-testid="add-application-btn">
                      <Plus className="h-4 w-4 mr-2" /> Add Application
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading">Add Application</DialogTitle>
                      <DialogDescription>Manually add an application</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateApplication} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Job Position</Label>
                        <Select value={appFormData.job_id} onValueChange={v => setAppFormData({ ...appFormData, job_id: v })}>
                          <SelectTrigger className="input-field" data-testid="app-job-select"><SelectValue placeholder="Select job" /></SelectTrigger>
                          <SelectContent>
                            {jobs.filter(j => j.status === "open").map(j => (
                              <SelectItem key={j.id} value={j.id}>
                                {j.title} {j.department_id ? `(${getDeptName(j.department_id)})` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Applicant Name</Label>
                        <Input value={appFormData.applicant_name} onChange={e => setAppFormData({ ...appFormData, applicant_name: e.target.value })}
                          placeholder="John Doe" className="input-field" required data-testid="app-name-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input type="email" value={appFormData.applicant_email} onChange={e => setAppFormData({ ...appFormData, applicant_email: e.target.value })}
                            placeholder="john@email.com" className="input-field" required data-testid="app-email-input" />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input value={appFormData.applicant_phone} onChange={e => setAppFormData({ ...appFormData, applicant_phone: e.target.value })}
                            placeholder="+91 98765 43210" className="input-field" data-testid="app-phone-input" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Cover Letter</Label>
                        <Textarea value={appFormData.cover_letter} onChange={e => setAppFormData({ ...appFormData, cover_letter: e.target.value })}
                          placeholder="Cover letter..." className="input-field min-h-[80px]" data-testid="app-cover-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Resume / CV</Label>
                        <div className="flex items-center gap-2">
                          <Input type="file" ref={resumeRef} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            className="input-field text-sm file:mr-3 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-medium file:bg-primary/10 file:text-primary"
                            data-testid="app-resume-input" />
                        </div>
                        <p className="text-xs text-slate-400">PDF, DOC, DOCX, JPG, PNG — Max 5 MB</p>
                      </div>
                      <Button type="submit" className="w-full btn-primary" data-testid="save-app-btn">Add Application</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <Card className="card-soft" data-testid="applications-table">
              <CardContent className="pt-6">
                {activeApps.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    {applications.filter(a => ACTIVE_STATUSES.includes(a.status)).length === 0
                      ? "No active applications. Add one manually or import via CSV."
                      : "No applications match the selected filters."}
                  </div>
                ) : (
                  <ApplicationTable apps={activeApps} testId="active-apps" />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Archived Tab with sub-tabs */}
        <TabsContent value="archived">
          <Tabs defaultValue="closed-jobs" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <TabsList className="bg-slate-50 border border-slate-200 p-1 rounded-lg">
                <TabsTrigger value="closed-jobs" className="rounded-md text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="archive-closed-jobs-tab">
                  <Briefcase className="h-3.5 w-3.5 mr-1.5" />
                  Closed Jobs ({closedJobs.length})
                </TabsTrigger>
                <TabsTrigger value="archived-apps" className="rounded-md text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="archive-applications-tab">
                  <UserCheck className="h-3.5 w-3.5 mr-1.5" />
                  Hired / Rejected ({archivedApps.length})
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-3 py-1">
                  <UserCheck className="h-3.5 w-3.5 mr-1.5" />
                  {hiredCount} Hired
                </Badge>
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 px-3 py-1">
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  {rejectedCount} Rejected
                </Badge>
              </div>
            </div>

            {/* Closed Jobs Sub-Tab */}
            <TabsContent value="closed-jobs">
              <Card className="card-soft" data-testid="closed-jobs-section">
                <CardContent className="pt-5">
                  {closedJobs.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Briefcase className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      No closed positions yet. Jobs you close will appear here.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {closedJobs.map(job => {
                        const createdDate = job.created_at ? new Date(job.created_at) : null;
                        const closedDate = job.closed_at ? new Date(job.closed_at) : null;
                        const daysToFill = createdDate && closedDate ? Math.round((closedDate - createdDate) / (1000 * 60 * 60 * 24)) : null;
                        const fmtDate = (d) => d ? d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
                        return (
                          <div key={job.id} className="p-3 rounded-lg border border-slate-100 bg-slate-50/50" data-testid={`closed-job-${job.id}`}>
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-medium text-slate-600 text-sm">{job.title}</p>
                              <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]">Closed</Badge>
                            </div>
                            <div className="flex gap-1.5 flex-wrap">
                              {job.department_id && <span className="text-[10px] text-slate-400">{getDeptName(job.department_id)}</span>}
                              {job.location && <span className="text-[10px] text-slate-400">| {job.location}</span>}
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
                              <span>Opened: {fmtDate(createdDate)}</span>
                              <span>Closed: {fmtDate(closedDate)}</span>
                            </div>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-slate-300">{applications.filter(a => a.job_id === job.id).length} application(s)</span>
                              {daysToFill !== null && (
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${daysToFill <= 30 ? "bg-green-100 text-green-700" : daysToFill <= 60 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}
                                  data-testid={`hiring-speed-${job.id}`}>
                                  {daysToFill} day{daysToFill !== 1 ? "s" : ""} to fill
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Archived Applications Sub-Tab */}
            <TabsContent value="archived-apps">
              <div className="space-y-4">
                <FilterBar deptFilter={appDeptFilter} setDeptFilter={setAppDeptFilter}
                  desigFilter={appDesigFilter} setDesigFilter={setAppDesigFilter} testPrefix="archive" />
                <Card className="card-soft" data-testid="archived-table">
                  <CardContent className="pt-6">
                    {archivedApps.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Archive className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                        No archived applications yet. Applications marked as "Hired" or "Rejected" will appear here.
                      </div>
                    ) : (
                      <ApplicationTable apps={archivedApps} showArchiveBadge testId="archived-apps" />
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* Edit Application Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="h-4 w-4 text-primary" /> Edit Application
            </DialogTitle>
            <DialogDescription>Update applicant details</DialogDescription>
          </DialogHeader>
          {editApp && (
            <form onSubmit={handleEditApplication} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Job Position</Label>
                <Select value={editApp.job_id} onValueChange={v => setEditApp({ ...editApp, job_id: v })}>
                  <SelectTrigger className="input-field" data-testid="edit-app-job-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {jobs.map(j => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.title} {j.department_id ? `(${getDeptName(j.department_id)})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Applicant Name</Label>
                <Input value={editApp.applicant_name} onChange={e => setEditApp({ ...editApp, applicant_name: e.target.value })}
                  className="input-field" required data-testid="edit-app-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={editApp.applicant_email} onChange={e => setEditApp({ ...editApp, applicant_email: e.target.value })}
                    className="input-field" required data-testid="edit-app-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={editApp.applicant_phone || ""} onChange={e => setEditApp({ ...editApp, applicant_phone: e.target.value })}
                    className="input-field" data-testid="edit-app-phone-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cover Letter</Label>
                <Textarea value={editApp.cover_letter || ""} onChange={e => setEditApp({ ...editApp, cover_letter: e.target.value })}
                  className="input-field min-h-[80px]" data-testid="edit-app-cover-input" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5 text-amber-500" />
                  Notes / Interview Feedback
                </Label>
                <Textarea
                  value={editApp.notes || ""}
                  onChange={e => setEditApp({ ...editApp, notes: e.target.value })}
                  placeholder="Add internal notes, interview feedback, or comments..."
                  className="input-field min-h-[80px] bg-amber-50/30 border-amber-200 focus:border-amber-400"
                  data-testid="edit-app-notes-input"
                />
                <p className="text-xs text-slate-400">Internal only — not visible to applicants</p>
              </div>
              <div className="space-y-2">
                <Label>Resume / CV</Label>
                {editApp.resume_filename ? (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200">
                    <Paperclip className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-emerald-700 font-medium truncate flex-1" data-testid="edit-app-resume-name">{editApp.resume_filename}</span>
                    <button type="button" onClick={() => handleDownloadResume(editApp.id)}
                      className="text-emerald-600 hover:text-emerald-800 p-1" data-testid="edit-app-download-resume">
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={() => handleDeleteResume(editApp.id)}
                      className="text-red-400 hover:text-red-600 p-1" data-testid="edit-app-delete-resume">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Input type="file" ref={editResumeRef} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="input-field text-sm file:mr-3 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-medium file:bg-primary/10 file:text-primary"
                      data-testid="edit-app-resume-input" />
                    <p className="text-xs text-slate-400 mt-1">PDF, DOC, DOCX, JPG, PNG — Max 5 MB</p>
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-edit-app-btn">Save Changes</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Recruitment;
